"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/compte";

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  async function handleSubmit(formData: FormData) {
    setLoading(true);
    setError("");

    const email = String(formData.get("email") || "");
    const password = String(formData.get("password") || "");

    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
      callbackUrl,
    });

    setLoading(false);

    if (!result?.ok) {
      setError("Email ou mot de passe incorrect.");
      return;
    }

    router.push(callbackUrl);
    router.refresh();
  }

  async function handleGoogleSignIn() {
    setError("");
    setGoogleLoading(true);

    try {
      await signIn("google", {
        callbackUrl,
      });
    } catch {
      setError("Impossible de se connecter avec Google pour le moment.");
      setGoogleLoading(false);
    }
  }

  return (
    <div className="rounded-[30px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-6 shadow-[var(--shadow)] sm:p-8">
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={googleLoading || loading}
        className="inline-flex w-full items-center justify-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 text-sm font-medium text-[var(--foreground)] transition hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className="h-5 w-5"
          aria-hidden="true"
        >
          <path
            fill="#EA4335"
            d="M12 10.2v3.9h5.5c-.2 1.3-1.5 3.8-5.5 3.8-3.3 0-6-2.8-6-6.2s2.7-6.2 6-6.2c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 2.8 14.7 2 12 2 6.9 2 2.8 6.4 2.8 11.7S6.9 21.4 12 21.4c6.9 0 8.6-4.9 8.6-7.4 0-.5 0-.9-.1-1.3H12Z"
          />
          <path
            fill="#34A853"
            d="M2.8 11.7c0 1.7.6 3.2 1.7 4.4l3-2.4c-.4-.6-.7-1.3-.7-2s.2-1.4.7-2l-3-2.4c-1.1 1.2-1.7 2.7-1.7 4.4Z"
          />
          <path
            fill="#4A90E2"
            d="M12 21.4c2.7 0 4.9-.9 6.5-2.5l-3.2-2.6c-.9.6-2 .9-3.3.9-2.5 0-4.7-1.7-5.4-4l-3.1 2.4c1.5 3.4 4.8 5.8 8.5 5.8Z"
          />
          <path
            fill="#FBBC05"
            d="M6.6 13.2c-.2-.5-.3-1-.3-1.5s.1-1 .3-1.5l-3.1-2.4c-.5 1-.7 2.1-.7 3.4s.2 2.3.7 3.4l3.1-2.4Z"
          />
        </svg>
        {googleLoading ? "Redirection vers Google..." : "Continuer avec Google"}
      </button>

      <div className="my-6 flex items-center gap-4">
        <div className="h-px flex-1 bg-[var(--border)]" />
        <span className="text-xs uppercase tracking-[0.18em] text-[var(--foreground)]/45">
          ou
        </span>
        <div className="h-px flex-1 bg-[var(--border)]" />
      </div>

      <form action={handleSubmit} className="contents">
        <div>
          <label
            htmlFor="email"
            className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
          >
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
          />
        </div>

        <div className="mt-5">
          <label
            htmlFor="password"
            className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
          >
            Mot de passe
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
          />
        </div>

        {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}

        <button
          type="submit"
          disabled={loading || googleLoading}
          className="mt-6 rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? "Connexion..." : "Se connecter"}
        </button>
      </form>
    </div>
  );
}